import { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, TextField, Snackbar } from '@mui/material';
import { PlayArrow, Pause, Refresh } from '@mui/icons-material';

const DEFAULT_POMODORO = 25;
const DEFAULT_SHORT_BREAK = 5;
const DEFAULT_LONG_BREAK = 15;

export default function PomodoroTimer() {
  const [customTime, setCustomTime] = useState(() => {
    return parseInt(localStorage.getItem('pomodoroCustomTime')) || DEFAULT_POMODORO;
  });
  const [time, setTime] = useState(() => {
    return parseInt(localStorage.getItem('pomodoroTime')) || DEFAULT_POMODORO * 60;
  });
  const [isRunning, setIsRunning] = useState(() => {
    return localStorage.getItem('pomodoroIsRunning') === 'true';
  });
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('pomodoroMode') || 'pomodoro';
  });
  const [pomodoroCount, setPomodoroCount] = useState(() => {
    return parseInt(localStorage.getItem('pomodoroCount')) || 0;
  });
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

  // Persist state changes
  useEffect(() => {
    localStorage.setItem('pomodoroCustomTime', customTime);
    localStorage.setItem('pomodoroTime', time);
    localStorage.setItem('pomodoroIsRunning', isRunning);
    localStorage.setItem('pomodoroMode', mode);
    localStorage.setItem('pomodoroCount', pomodoroCount);
  }, [customTime, time, isRunning, mode, pomodoroCount]);

  // Timer logic
  const [worker, setWorker] = useState(null);

  useEffect(() => {
    const newWorker = new Worker(new URL('../../workers/timerWorker.js', import.meta.url));
    
    newWorker.onmessage = (e) => {
      if (e.data.type === 'tick' && isRunning) {
        setTime((prevTime) => {
          if (prevTime <= 1) {
            setIsRunning(false);
            handleTimerComplete();
            return 0;
          }
          return prevTime - 1;
        });
      }
    };

    setWorker(newWorker);

    return () => newWorker.terminate();
  }, []);

  useEffect(() => {
    if (worker) {
      if (isRunning && time > 0) {
        worker.postMessage({ type: 'start' });
      } else {
        worker.postMessage({ type: 'stop' });
      }
    }
  }, [isRunning, worker]);

  const handleTimerComplete = () => {
    if (mode === 'pomodoro') {
      setPomodoroCount(prev => {
        const newCount = prev + 1;
        if (newCount % 4 === 0) {
          setMode('longBreak');
          setTime(DEFAULT_LONG_BREAK * 60);
        } else {
          setMode('shortBreak');
          setTime(DEFAULT_SHORT_BREAK * 60);
        }
        return newCount;
      });
    } else {
      setMode('pomodoro');
      setTime(customTime * 60);
    }

    if (notificationPermission === "granted") {
      const notification = new Notification("Pomodoro Timer", {
        body: `${mode === 'pomodoro' ? 'Time for a break!' : 'Break is over!'}\nClick to snooze for 5 minutes`,
        icon: "/favicon.svg",
        requireInteraction: true,
        silent: false
      });

      notification.onclick = () => {
        setTime(300); // 5 minutes snooze
        setIsRunning(true);
        notification.close();
      };
    }
  };

  useEffect(() => {
    if (notificationPermission !== 'granted') {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission);
      });
    }
  }, []);


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return { mins, secs };
  };

  const handleModeChange = (newMode) => {
    setIsRunning(false);
    setMode(newMode);
    switch (newMode) {
      case 'pomodoro':
        setTime(customTime * 60);
        break;
      case 'shortBreak':
        setTime(DEFAULT_SHORT_BREAK * 60);
        break;
      case 'longBreak':
        setTime(DEFAULT_LONG_BREAK * 60);
        break;
    }
  };

  const handleCustomTimeChange = (event) => {
    const value = Math.max(1, Math.min(60, parseInt(event.target.value) || DEFAULT_POMODORO));
    setCustomTime(value);
    if (mode === 'pomodoro' && !isRunning) {
      setTime(value * 60);
    }
  };

  const { mins, secs } = formatTime(time);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Pomodoro Timer
      </Typography>

      <Stack direction="row" spacing={2} justifyContent="center" mb={4}>
        <Button
          variant={mode === 'pomodoro' ? 'contained' : 'outlined'}
          onClick={() => handleModeChange('pomodoro')}
        >
          Pomodoro
        </Button>
        <Button
          variant={mode === 'shortBreak' ? 'contained' : 'outlined'}
          onClick={() => handleModeChange('shortBreak')}
        >
          Short Break
        </Button>
        <Button
          variant={mode === 'longBreak' ? 'contained' : 'outlined'}
          onClick={() => handleModeChange('longBreak')}
        >
          Long Break
        </Button>
      </Stack>

      {mode === 'pomodoro' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <TextField
            type="number"
            label="Custom Minutes"
            value={customTime}
            onChange={handleCustomTimeChange}
            disabled={isRunning}
            InputProps={{ inputProps: { min: 1, max: 60 } }}
            size="small"
            sx={{ width: 150 }}
          />
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          mx: 'auto',
          textAlign: 'center',
          borderRadius: 2,
          bgcolor: 'background.paper'
        }}
      >
        <div className="grid grid-flow-col gap-5 text-center auto-cols-max justify-center">
          <div className="flex flex-col p-2 bg-neutral rounded-box text-neutral-content">
            <span className="countdown font-mono text-6xl">
              <span style={{ "--value": mins }}></span>
            </span>
            minutes
          </div>
          <div className="flex flex-col p-2 bg-neutral rounded-box text-neutral-content">
            <span className="countdown font-mono text-6xl">
              <span style={{ "--value": secs }}></span>
            </span>
            seconds
          </div>
        </div>

        <Stack direction="row" spacing={2} justifyContent="center" mt={4}>
          <IconButton
            onClick={() => setIsRunning(!isRunning)}
            color="primary"
            size="large"
          >
            {isRunning ? <Pause /> : <PlayArrow />}
          </IconButton>
          <IconButton
            onClick={() => {
              setIsRunning(false);
              handleModeChange(mode);
            }}
            color="secondary"
            size="large"
          >
            <Refresh />
          </IconButton>
        </Stack>

        <Typography variant="body1" mt={2}>
          Completed Pomodoros: {pomodoroCount}
        </Typography>
      </Paper>
    </Box>
  );
}